import { MainRoutes } from "@router/route-params";
import { BaseProjectProps } from "@custom-types/project-types";
import { useAppSelector } from "@store/store-helper";
import { isDevModeEnabledSelector } from "@store/app/app-selector";
import { isProjectArchiveSelector } from "@store/projects/projects-selector";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { FaroButtonContained } from "@components/common/faro-button-contained";
import { PreservingNavLink } from "@components/common/preserve-navigation/preserving-nav-link";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { colorConst, sphereColors } from "@styles/common-colors";
import { FaroPopover } from "@faro-lotv/flat-ui";
import { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import manageDataGif from "@assets/manage-data/manage_data.gif";
import { DEFAULT_TEXT_FONT_WEIGHT } from "@styles/common-styles";
import { ExternalLinks } from "@components/external-links";
import { SplashScreenSdbTopic } from "@components/splash-screen/splash-screen-data";
import { useTranslate } from "@faro-lotv/foreign-observers";
import { isAlphaTestingEnabledSelector } from "@store/app/app-selector";

interface Props extends BaseProjectProps {
  /** Flag to show a loading state */
  isLoading?: boolean;
}

/**
 *  Button to open staging area of a project for data management
 */
export function DataManagementButton({
  project,
  isLoading,
}: Props): JSX.Element | null {
  const { trackEvent } = useTrackEvent();
  const translate = useTranslate();

  const popoverRefs = useRef<HTMLDivElement | HTMLAnchorElement | null>(null);
  const [isShowPopover, setIsShowPopover] = useState<boolean>(false);

  const { canViewDataManagement } = useHasUserValidPermissionProjectLevel({
    project,
  });
  const isProjectArchived = useAppSelector(
    isProjectArchiveSelector(project.id)
  );

  const hasRegularAccess = canViewDataManagement && !isProjectArchived;
  const isDevModeEnabled = useAppSelector(isDevModeEnabledSelector);
  const isDevOnly = !hasRegularAccess && isDevModeEnabled;
  const isAlphaTestingEnabled = useAppSelector(isAlphaTestingEnabledSelector);

  /**
   * Returning null if:
   * - Loading
   * - (No beta flag, or no permission, or project is archived) and not (dev mode).
   */
  if (isLoading || (!hasRegularAccess && !isDevModeEnabled)) {
    return null;
  }

  const sx = isDevOnly
    ? {
        color: colorConst.devFeature,
        borderColor: colorConst.devFeature,
        "&:hover": {
          color: colorConst.devFeature,
          borderColor: colorConst.devFeature,
          backgroundColor: sphereColors.gray100,
          filter: "brightness(90%)",
        },
      }
    : undefined;

  const learMoreAbout = translate("Learn more about");
  const descriptionPopOver = isAlphaTestingEnabled
    ? translate(`This is your dedicated space to add and manage your scans within
              your project. Upload, process, register, and publish your data all
              in one place, automatically.`)
    : translate(`This is your dedicated space to add and manage Blink scans within
              your project. Upload, process, register, and publish your data all
              in one place, automatically.`);
  return (
    <Box
      ref={popoverRefs}
      onMouseEnter={() => setIsShowPopover(true)}
      onMouseLeave={() => setIsShowPopover(false)}
    >
      <PreservingNavLink to={`../${MainRoutes.dataManagement}`}>
        <FaroButtonContained
          sx={sx}
          onClick={() => {
            trackEvent({
              name: ProjectEvents.openDataManagement,
              props: {
                origin: "projectDetails",
              },
            });
          }}
        >
          Manage data
        </FaroButtonContained>
      </PreservingNavLink>

      <FaroPopover
        dark
        title="Manage data"
        description={
          <>
            <Box
              component="img"
              src={manageDataGif}
              alt="data management popover"
            />
            <Typography
              fontSize="0.75rem"
              fontWeight={DEFAULT_TEXT_FONT_WEIGHT}
            >
              {descriptionPopOver}
            </Typography>
            {/* Might be extended later with some Orbis and Focus information. */}
            <Typography fontSize="0.75rem" marginTop="1rem" lineHeight="1rem">
              {learMoreAbout}{" "}
              <ExternalLinks
                text="Blink"
                link={"blinkLaunch"}
                sx={{
                  color: sphereColors.pureWhite,
                  textDecorationColor: sphereColors.pureWhite,
                }}
                trackEventProps={{
                  topic: SplashScreenSdbTopic.blinkLaunch,
                  link: "blinkLaunch",
                }}
              />
            </Typography>
          </>
        }
        anchorEl={popoverRefs.current}
        open={isShowPopover}
        onClose={() => setIsShowPopover(false)}
      />
    </Box>
  );
}
